import {
	createArrayAtomName,
	createArrayItemAtomName,
	createAtomNameFromOptions,
	NameOptions,
} from "../extras/reactive-names";
import {
	getArraySetChanges,
	getArrayDeleteChanges,
} from "../extras/reactive-plugin-array";
import { afterChange, beforeChange } from "../extras/reactive-plugin";
import { createAtom } from "../primitives/reactive-atom";
import { reactiveValueEquals } from "../primitives/reactive-options";
import { Atom } from "../primitives/reactive-primitive-types";
import { ContextReactiveOptions } from "./reactive-abstraction-types";
import { getChild, getChildOptions } from "./reactive-children";
import { createProxy, ensureInert } from "./reactive-proxy";

type ProxyTarget = unknown[];

type Reactive = {
	items: Atom;
};

export const createReactiveArray = <T extends unknown[]>(
	inertArr: T,
	reactiveOptions?: ContextReactiveOptions,
	nameOptions?: NameOptions
): T => {
	const atomName = createAtomNameFromOptions(nameOptions);

	const proxy = new Proxy(
		inertArr,
		new ReactiveArrayProxyHandler(
			atomName,
			{
				items: createAtom(createArrayAtomName(atomName)),
			},
			() => proxy,
			reactiveOptions
		)
	) as T;

	return proxy;
};

class ReactiveArrayProxyHandler implements ProxyHandler<ProxyTarget> {
	#atomName: string;
	#reactive: Reactive;
	#getProxy: () => unknown[];
	#options: { reactiveOptions?: ContextReactiveOptions };

	constructor(
		atomName: string,
		reactive: Reactive,
		getProxy: () => unknown[],
		reactiveOptions?: ContextReactiveOptions
	) {
		this.#atomName = atomName;
		this.#reactive = reactive;
		this.#getProxy = getProxy;
		this.#options = { reactiveOptions };
	}

	get(inertArr: ProxyTarget, property: string | symbol, receiver: unknown) {
		const p = property as number | string | "length";
		const ownPropertyDescriptor = Reflect.getOwnPropertyDescriptor(inertArr, p);
		const isInherited = Reflect.has(inertArr, p) && !ownPropertyDescriptor;
		if (!isInherited) {
			this.#reactive.items.reportObserved();
		}
		const result = Reflect.get(inertArr, p, receiver);
		return getChild(this.#options, result, () =>
			createProxy(result, getChildOptions(this.#options), {
				parentAtomName: this.#atomName,
				createAtomName: createArrayItemAtomName,
				childIndex: p.toString(),
			})
		);
	}

	set(
		inertArr: ProxyTarget,
		property: string | symbol,
		newValue: any,
		receiver: any
	): boolean {
		const p = property as number | "length";
		const hasProperty = inertArr.hasOwnProperty(property);
		const newStoredValue =
			this.#options.reactiveOptions?.deep === false
				? newValue
				: ensureInert(newValue);

		const currentValue = Reflect.get(inertArr, property, receiver);
		const isChanged = !reactiveValueEquals(currentValue, newValue);
		const previousLength = inertArr.length;

		const willChangeForPlugins =
			this.#hasPlugins() &&
			((isChanged && p !== "length") ||
				(p === "length" && Number(newValue) !== previousLength) ||
				!hasProperty);
		const changes = !willChangeForPlugins
			? null
			: getArraySetChanges(
					this.#getProxy(),
					inertArr,
					p,
					currentValue,
					newStoredValue,
					previousLength
			  );

		if (changes) {
			beforeChange(changes, this.#options.reactiveOptions?.plugins);
		}

		const result = Reflect.set(inertArr, property, newStoredValue, receiver);

		const isLengthChanged = previousLength !== inertArr.length;
		if (result) {
			// The length property does not automatically get trapped when, for example,
			// setting the value like this: `array[array.length] = 10`, because there
			// is no internal call to a function where `this` refers to a proxy; hence,
			// we have to manually check it after applying the update
			if ((isChanged && p !== "length") || isLengthChanged || !hasProperty) {
				this.#reactive.items.reportChanged();
			}
		}

		if (changes) {
			afterChange(changes, this.#options.reactiveOptions?.plugins);
		}

		return result;
	}

	deleteProperty(inertArr: ProxyTarget, property: string | symbol) {
		const p = property as number | string | "length"; // p can be 'length' or a number here
		const hasProperty = inertArr.hasOwnProperty(property);
		const currentValue = Reflect.get(inertArr, property);
		const isChanged = !reactiveValueEquals(currentValue, undefined);
		const previousLength = inertArr.length;

		const changes =
			!this.#hasPlugins() || !isChanged
				? null
				: getArrayDeleteChanges(this.#getProxy(), p, currentValue);

		if (changes) {
			beforeChange(changes, this.#options.reactiveOptions?.plugins);
		}

		const result = Reflect.deleteProperty(inertArr, property);

		const isLengthChanged = previousLength !== inertArr.length;
		if (result) {
			if ((isChanged && p !== "length") || isLengthChanged || hasProperty) {
				this.#reactive.items.reportChanged();
			}
		}

		if (changes) {
			afterChange(changes, this.#options.reactiveOptions?.plugins);
		}

		return result;
	}

	ownKeys(inertArr: ProxyTarget) {
		this.#reactive.items.reportObserved();
		return Reflect.ownKeys(inertArr);
	}

	#hasPlugins() {
		return (this.#options.reactiveOptions?.plugins?.length ?? 0) > 0;
	}
}
